import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import {
  rolesApi,
  usersApi,
  type AppUserRow,
  type CreateUserBody,
  type UpdateUserBody,
} from '@/api/users';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const usersKey = (params: object) => ['users', 'list', params] as const;
const rolesKey = ['users', 'roles'] as const;

export function UsersPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('USER_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('USER_MGMT:EDIT'));

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [roleId, setRoleId] = useState<number | undefined>();
  const [active, setActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debounced, roleId, active]);

  const params = {
    q: debounced || undefined,
    roleId,
    active: active === 'all' ? undefined : active === 'active',
    page,
    size,
  };

  const { data, isFetching } = useQuery({
    queryKey: usersKey(params),
    queryFn: () => usersApi.list(params),
    placeholderData: (prev) => prev,
  });

  const { data: roles } = useQuery({
    queryKey: rolesKey,
    queryFn: rolesApi.list,
    staleTime: 5 * 60 * 1000,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AppUserRow | null>(null);
  const [resetting, setResetting] = useState<AppUserRow | null>(null);
  const [createForm] = Form.useForm<CreateFormShape>();
  const [editForm] = Form.useForm<EditFormShape>();
  const [resetForm] = Form.useForm<{ newPassword: string }>();

  const create = useMutation({
    mutationFn: (body: CreateUserBody) => usersApi.create(body),
    onSuccess: () => {
      message.success('User created');
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      setCreateOpen(false);
      createForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateUserBody }) => usersApi.update(id, body),
    onSuccess: () => {
      message.success('User updated');
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      setEditing(null);
      editForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const resetPwd = useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) =>
      usersApi.resetPassword(id, newPassword),
    onSuccess: () => {
      message.success('Password reset; user must change it on next login');
      setResetting(null);
      resetForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => usersApi.setActive(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'list'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openEdit = (row: AppUserRow) => {
    setEditing(row);
    editForm.setFieldsValue({
      roleId: row.roleId,
      staffId: row.staffId,
      active: row.active,
    });
  };

  const columns: TableProps<AppUserRow>['columns'] = [
    {
      title: 'Username',
      dataIndex: 'username',
      render: (u: string, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{u}</Typography.Text>
          {row.mustChangePassword && (
            <Typography.Text type="warning" style={{ fontSize: 11 }}>
              Must change password on next login
            </Typography.Text>
          )}
        </Space>
      ),
    },
    { title: 'Role', dataIndex: 'roleName', width: 180, render: (n: string) => <Tag>{n}</Tag> },
    {
      title: 'Staff link',
      dataIndex: 'staffId',
      width: 120,
      render: (id: number | null) =>
        id == null ? <Typography.Text type="secondary">—</Typography.Text> : `#${id}`,
    },
    {
      title: 'Last login',
      dataIndex: 'lastLoginAt',
      width: 170,
      render: (d: string | null) =>
        d ? (
          <Typography.Text>{dayjs(d).format('D MMM YYYY HH:mm')}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">never</Typography.Text>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (val: boolean, row) => (
        <Switch
          size="small"
          checked={val}
          disabled={!canEdit || (toggleActive.isPending && toggleActive.variables?.id === row.id)}
          onChange={(v) => toggleActive.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 230,
      align: 'right',
      render: (_, row) => (
        <Space>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          )}
          {canEdit && (
            <Button
              size="small"
              type="text"
              icon={<KeyOutlined />}
              onClick={() => setResetting(row)}
            >
              Reset password
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module"
        title="User management"
        description="Application accounts, their assigned role, and login state."
        count={data ? { value: data.totalElements, label: 'users' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              New user
            </Button>
          ) : null
        }
      />

      <Space wrap size={12} style={{ rowGap: 12 }}>
        <Segmented
          options={[
            { label: 'Users', value: 'users' },
            { label: 'Roles & permissions', value: 'roles' },
          ]}
          value="users"
          onChange={(val) => {
            if (val === 'roles') navigate('/users/roles');
          }}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by username"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          placeholder="All roles"
          allowClear
          value={roleId}
          onChange={setRoleId}
          options={(roles ?? []).map((r) => ({ value: r.id, label: r.name }))}
          style={{ minWidth: 180 }}
        />
        <Select
          value={active}
          onChange={setActive}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' },
          ]}
          style={{ minWidth: 160 }}
        />
      </Space>

      <Table
        rowKey="id"
        size="middle"
        loading={isFetching}
        columns={columns}
        dataSource={data?.rows ?? []}
        pagination={{
          current: (data?.page ?? page) + 1,
          pageSize: data?.size ?? size,
          total: data?.totalElements ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          onChange: (next, nextSize) => {
            setPage(next - 1);
            if (nextSize !== size) setSize(nextSize);
          },
        }}
      />

      <Modal
        title="New user"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={create.isPending}
        okText="Create"
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ password: '' }}
          onFinish={(values: CreateFormShape) =>
            create.mutate({
              username: values.username.trim(),
              password: values.password,
              roleId: values.roleId,
              staffId: values.staffId ?? null,
            })
          }
        >
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, min: 3, max: 50 }]}
          >
            <Input autoFocus placeholder="e.g. ahmed.khan" />
          </Form.Item>
          <Form.Item
            label="Initial password"
            name="password"
            rules={[{ required: true, min: 8, max: 200 }]}
            extra="The user is forced to change it on first login."
          >
            <Input.Password />
          </Form.Item>
          <Form.Item label="Role" name="roleId" rules={[{ required: true }]}>
            <Select
              placeholder="Pick a role"
              options={(roles ?? [])
                .filter((r) => r.active)
                .map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
          <Form.Item label="Linked staff ID" name="staffId" tooltip="Optional. Link to an HR staff record.">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editing ? `Edit ${editing.username}` : 'Edit user'}
        open={editing != null}
        onCancel={() => setEditing(null)}
        onOk={() => editForm.submit()}
        confirmLoading={update.isPending}
        okText="Save"
        destroyOnHidden
      >
        {editing && (
          <EditUserForm
            form={editForm}
            roles={(roles ?? []).map((r) => ({ value: r.id, label: r.name }))}
            onSubmit={(values) =>
              update.mutate({
                id: editing.id,
                body: { roleId: values.roleId, staffId: values.staffId ?? null, active: values.active },
              })
            }
          />
        )}
      </Modal>

      <Modal
        title={resetting ? `Reset password for ${resetting.username}` : 'Reset password'}
        open={resetting != null}
        onCancel={() => setResetting(null)}
        onOk={() => resetForm.submit()}
        confirmLoading={resetPwd.isPending}
        okText="Reset"
        okButtonProps={{ danger: true }}
        destroyOnHidden
      >
        {resetting && (
          <ResetPasswordForm
            form={resetForm}
            onSubmit={(values) =>
              resetPwd.mutate({ id: resetting.id, newPassword: values.newPassword })
            }
          />
        )}
      </Modal>
    </div>
  );
}

function EditUserForm({
  form,
  roles,
  onSubmit,
}: {
  form: ReturnType<typeof Form.useForm<EditFormShape>>[0];
  roles: { value: number; label: string }[];
  onSubmit: (values: EditFormShape) => void;
}) {
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Form.Item label="Role" name="roleId" rules={[{ required: true }]}>
        <Select options={roles} />
      </Form.Item>
      <Form.Item label="Linked staff ID" name="staffId">
        <InputNumber min={1} style={{ width: '100%' }} placeholder="Optional" />
      </Form.Item>
      <Form.Item label="Active" name="active" valuePropName="checked">
        <Switch />
      </Form.Item>
    </Form>
  );
}

function ResetPasswordForm({
  form,
  onSubmit,
}: {
  form: ReturnType<typeof Form.useForm<{ newPassword: string }>>[0];
  onSubmit: (values: { newPassword: string }) => void;
}) {
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Form.Item
        label="New password"
        name="newPassword"
        rules={[{ required: true, min: 8, max: 200 }]}
        extra="The user must change it on next login."
      >
        <Input.Password autoFocus />
      </Form.Item>
    </Form>
  );
}

interface CreateFormShape {
  username: string;
  password: string;
  roleId: number;
  staffId?: number | null;
}

interface EditFormShape {
  roleId: number;
  staffId?: number | null;
  active: boolean;
}
